/**
 * Intercom service for the Angular admin client.
 *
 * Provides in-app support via Intercom's JavaScript widget.
 * The Intercom JS snippet must be loaded in index.html or via a script tag.
 */
import { Injectable } from '@angular/core';

/** Intercom configuration. */
const INTERCOM_APP_ID = 't2mwwy9r';

/**
 * Minimal type for the Intercom global function.
 * The full Intercom JS SDK types are available via @intercom/messenger-js-sdk
 * but we use the global `window.Intercom` to avoid a heavyweight dependency.
 */
declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- interface required for global Window augmentation (declaration merging does not work with type aliases)
  interface Window {
    Intercom?: (...args: unknown[]) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class IntercomService {
  private initialised = false;

  /**
   * Boot Intercom with the app ID.
   * Should be called once on app init.
   */
  boot(): void {
    if (this.initialised || typeof window === 'undefined') return;

    // Inject the Intercom script if not already present
    if (!window.Intercom) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;
      document.head.appendChild(script);

      // Set up the Intercom function stub
      (window as Window).Intercom = function (...args: unknown[]) {
        (window.Intercom as { q?: unknown[] }).q =
          (window.Intercom as { q?: unknown[] }).q || [];
        ((window.Intercom as { q?: unknown[] }).q as unknown[]).push(args);
      };
    }

    window.Intercom?.('boot', { app_id: INTERCOM_APP_ID });
    this.initialised = true;
  }

  /**
   * Register an identified user for support context.
   */
  registerUser(userId: string, email?: string, name?: string): void {
    if (!this.initialised) return;
    window.Intercom?.('update', {
      user_id: userId,
      email,
      name,
    });
  }

  /**
   * Open the Intercom messenger widget.
   */
  show(): void {
    if (!this.initialised) return;
    window.Intercom?.('show');
  }

  /**
   * Hide the Intercom messenger widget.
   */
  hide(): void {
    if (!this.initialised) return;
    window.Intercom?.('hide');
  }

  /**
   * Log out the current user from Intercom.
   */
  shutdown(): void {
    if (!this.initialised) return;
    window.Intercom?.('shutdown');
  }
}
